import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { TabbedChips } from '@cbhq/cds-web/chips/TabbedChips';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { TabNavigation } from '@cbhq/cds-web/tabs';
import { Link, TextBody, TextHeadline, TextTitle2 } from '@cbhq/cds-web/typography';

import {
  CDP_SDK_DOCS_LINK,
  COINBASE_CDP_SDK_DISCORD_LINK,
  MPC_WALLET_QUICKSTART_LINK,
  ONCHAIN_KIT_APP_TEMPLATE_LINK,
  ONCHAIN_KIT_LINK,
  PLATFORM_APIS_DOCS_LINK,
} from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { AppRoute } from ':cloud/utils/routes';
import { BorderedStepNumber } from ':cloud/widgets/dashboard/BorderedStepNumber';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents';

const sdkTabs = [
  {
    id: 'backend',
    label: 'Backend',
  },
  {
    id: 'frontend',
    label: 'Frontend',
  },
];

const sdkSubTabs = {
  backend: [
    {
      id: 'typescript',
      label: 'Typescript',
    },
    {
      id: 'ruby',
      label: 'Ruby',
    },
    {
      id: 'python',
      label: 'Python',
    },
  ],
  frontend: [
    {
      id: 'npm',
      label: 'npm',
    },
    {
      id: 'yarn',
      label: 'yarn',
    },
    {
      id: 'pnpm',
      label: 'pnpm',
    },
    {
      id: 'bun',
      label: 'bun',
    },
  ],
};

function Step1() {
  const history = useHistory();
  const handleAPIKeyPress = useCallback(() => {
    history.push(AppRoute.Projects.APIKeys);
  }, [history]);
  return (
    <HStack spacing={5} borderRadius="roundedLarge" background="backgroundAlternate" gap={5}>
      <BorderedStepNumber stepNumber="1" />
      <VStack gap={0.5} flexGrow={1}>
        <TextHeadline as="p">Create API Key</TextHeadline>
        <TextBody as="p" color="foregroundMuted">
          Your API key will allow you to use any product on the platform.
        </TextBody>
      </VStack>
      <HStack alignItems="center">
        <Button variant="primary" onPress={handleAPIKeyPress}>
          Create API Key
        </Button>
      </HStack>
    </HStack>
  );
}

function Step2({ activeTab, setActiveTab }) {
  const [activeSubTab, setActiveSubTab] = useState('typescript');

  useEffect(() => {
    setActiveSubTab(sdkSubTabs[activeTab][0].id);
  }, [activeTab]);

  const subTabsContent = useMemo(
    () => ({
      typescript: 'npm install @coinbase/coinbase-sdk',
      ruby: 'gem install coinbase-sdk',
      python: 'pip install cdp-sdk',
      npm: 'npm install @coinbase/onchainkit',
      yarn: 'yarn add @coinbase/onchainkit',
      pnpm: 'pnpm add @coinbase/onchainkit',
      bun: 'bun add @coinbase/onchainkit',
    }),
    [],
  );

  return (
    <HStack spacing={5} borderRadius="roundedLarge" background="backgroundAlternate" gap={5}>
      <BorderedStepNumber stepNumber="2" />
      <VStack gap={3} flexGrow={1}>
        <TextHeadline as="p">Install SDK</TextHeadline>
        <VStack gap={3} flexGrow={1}>
          <TabbedChips value={activeTab} onChange={setActiveTab} tabs={sdkTabs} />

          {activeTab === 'backend' ? (
            <TextBody as="p" color="foregroundMuted">
              For back-end development, use the CDP SDK to integrate Coinbase Platform APIs to
              store, send, trade, and stake your assets.{' '}
              <Link variant="body" to={CDP_SDK_DOCS_LINK} openInNewWindow underline={false}>
                Learn more
              </Link>
            </TextBody>
          ) : (
            <TextBody as="p" color="foregroundMuted">
              For front-end development, use the OnchainKit to build your onchain apps with
              ready-to-use React components like Smart Wallets.{' '}
              <Link variant="body" to={ONCHAIN_KIT_LINK} openInNewWindow underline={false}>
                Learn more
              </Link>
            </TextBody>
          )}

          <VStack borderRadius="rounded" overflow="hidden">
            <ThemeProvider scale="xSmall" spectrum="dark">
              <HStack
                dangerouslySetBackground="rgb(var(--gray20))"
                spacingHorizontal={3}
                spacingVertical={3}
                alignItems="center"
                justifyContent="space-between"
              >
                <TabNavigation
                  value={activeSubTab}
                  tabs={sdkSubTabs[activeTab]}
                  onChange={setActiveSubTab}
                  variant="secondary"
                  flexShrink={1} // prevent magic caret from appearing
                />
                <ClipboardIcon text={subTabsContent[activeSubTab]} color="background" />
              </HStack>

              <VStack
                dangerouslySetBackground="rgb(var(--gray15))"
                spacingHorizontal={3}
                spacingVertical={5}
              >
                <TextBody as="p" mono>
                  {subTabsContent[activeSubTab]}
                </TextBody>
              </VStack>
            </ThemeProvider>
          </VStack>
        </VStack>
      </VStack>
    </HStack>
  );
}

function Step3({ activeTab }) {
  return (
    <HStack spacing={5} borderRadius="roundedLarge" background="backgroundAlternate" gap={3}>
      <BorderedStepNumber stepNumber="3" />
      {activeTab === 'backend' ? (
        <>
          <VStack gap={0.5} flexGrow={1}>
            <TextHeadline as="p">Follow the MPC Wallets quickstart guide</TextHeadline>
            <TextBody as="p" color="foregroundMuted">
              CDP SDK allows you to create wallets and send funds onchain within minutes. In this
              quickstart, you will learn how to create a wallet, fund it with testnet ETH, transfer
              funds between wallets, and trade assets.
              <br />
              <br />
              Interested in building more?{' '}
              <Link variant="body" to={PLATFORM_APIS_DOCS_LINK} openInNewWindow underline={false}>
                Explore more quickstart guides
              </Link>
            </TextBody>
          </VStack>
          <HStack alignItems="center">
            <Button
              variant="primary"
              endIcon="diagonalUpArrow"
              onPress={onExternalLinkPress(MPC_WALLET_QUICKSTART_LINK)}
            >
              Get started
            </Button>
          </HStack>
        </>
      ) : (
        <>
          <VStack flexGrow={1}>
            <TextHeadline as="p">
              Get started with Smart Wallets using the Onchain App Template
            </TextHeadline>
            <TextBody as="p" color="foregroundMuted">
              An Onchain App Template built with{' '}
              <Link
                underline
                color="foregroundMuted"
                variant="body"
                to={ONCHAIN_KIT_LINK}
                openInNewWindow
              >
                OnchainKit
              </Link>
              , and ready to be deployed to Vercel.
            </TextBody>
          </VStack>
          <HStack alignItems="center">
            <Button
              variant="primary"
              endIcon="diagonalUpArrow"
              onPress={onExternalLinkPress(ONCHAIN_KIT_APP_TEMPLATE_LINK)}
            >
              Get started
            </Button>
          </HStack>
        </>
      )}
    </HStack>
  );
}

function Step4() {
  return (
    <HStack spacing={5} borderRadius="roundedLarge" background="backgroundAlternate" gap={3}>
      <BorderedStepNumber stepNumber="4" />
      <VStack gap={0.5} flexGrow={1}>
        <TextHeadline as="p">Get support</TextHeadline>
        <TextBody as="p" color="foregroundMuted">
          Need help or want to share what you&apos;re building? Join thousands of other developers
          on the CDP Discord community.
        </TextBody>
      </VStack>
      <HStack alignItems="center">
        <Button
          variant="primary"
          endIcon="diagonalUpArrow"
          onPress={onExternalLinkPress(COINBASE_CDP_SDK_DISCORD_LINK)}
        >
          CDP Discord
        </Button>
      </HStack>
    </HStack>
  );
}

export function ProjectDashboardGettingStarted() {
  const [activeTab, setActiveTab] = useState('backend');
  return (
    <VStack spacing={7} borderedTop>
      <TextTitle2 as="h2" spacingBottom={3}>
        Get started
      </TextTitle2>
      <VStack gap={3}>
        <Step1 />
        <Step2 activeTab={activeTab} setActiveTab={setActiveTab} />
        <Step3 activeTab={activeTab} />
        <Step4 />
      </VStack>
    </VStack>
  );
}
